// src/lib/multiplayer-v2.ts
// Proper server-authoritative multiplayer using RPC commands

import { getSupabaseClient, ensureSupabaseSession } from '@/lib/supabase';

export interface CommandResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  errorType?: 'unauthorized' | 'version_conflict' | 'invalid_state' | 'validation' | 'unknown';
  currentVersion?: number;
  needsRetry?: boolean;
}

/**
 * Generate idempotency key for commands
 */
function generateIdempotencyKey(matchId: string, command: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${matchId}-${command}-${timestamp}-${random}`;
}

/**
 * Execute a command RPC function with proper error handling
 */
async function executeCommand<T = unknown>(
  functionName: string,
  params: Record<string, unknown>,
  options: {
    idempotent?: boolean;
    matchId?: string;
  } = {}
): Promise<CommandResult<T>> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) {
    return {
      success: false,
      error: 'Not authenticated',
      errorType: 'unauthorized',
    };
  }

  // Add idempotency key if requested
  if (options.idempotent && options.matchId) {
    params.p_idempotency_key = generateIdempotencyKey(options.matchId, functionName);
  }

  const { data, error } = await supabase.rpc(functionName, params);

  if (error) {
    return {
      success: false,
      error: error.message,
      errorType: 'unknown',
    };
  }

  // Check for application-level errors
  if (data?.error) {
    const errorType =
      data.error === 'unauthorized'
        ? 'unauthorized'
        : data.error === 'version_conflict'
        ? 'version_conflict'
        : data.error.includes('not_in') || data.error.includes('already')
        ? 'invalid_state'
        : 'validation';

    return {
      success: false,
      error: data.error,
      errorType,
      currentVersion: data.current_version,
      needsRetry: errorType === 'version_conflict',
    };
  }

  return {
    success: true,
    data: data as T,
  };
}

/**
 * V2 Multiplayer API - Server-authoritative commands
 */

export interface BidSubmissionResult {
  bid: number;
  new_bid_version: number;
  coins_remaining: number;
}

export async function submitBidV2(
  matchId: string,
  bid: number,
  expectedBidVersion?: number
): Promise<CommandResult<BidSubmissionResult>> {
  return executeCommand<BidSubmissionResult>(
    'rpc_submit_bid_v2',
    {
      p_match_id: matchId,
      p_bid: bid,
      p_expected_bid_version: expectedBidVersion,
    },
    { idempotent: true, matchId }
  );
}

export interface BidLockResult {
  locked_bid: number;
  new_bid_version: number;
}

export async function lockBidV2(matchId: string): Promise<CommandResult<BidLockResult>> {
  return executeCommand<BidLockResult>(
    'rpc_lock_bid_v2',
    {
      p_match_id: matchId,
    },
    { idempotent: true, matchId }
  );
}

export interface AuctionResolutionResult {
  winner: 'host' | 'guest' | 'tie';
  character_id: string;
  host_bid: number;
  guest_bid: number;
  next_auction_index: number;
  new_state_version: number;
  timer_expired: boolean;
  rebid_required?: boolean;
  minimum_bid?: number;
  match_over?: boolean;
  match_winner?: 'host' | 'guest' | 'draw' | null;
}

export async function resolveAuctionV2(
  matchId: string,
  expectedStateVersion?: number
): Promise<CommandResult<AuctionResolutionResult>> {
  return executeCommand<AuctionResolutionResult>(
    'rpc_resolve_auction_v2',
    {
      p_match_id: matchId,
      p_expected_state_version: expectedStateVersion,
    },
    { idempotent: true, matchId }
  );
}

export interface AuctionTimerState {
  status: 'not_started' | 'active' | 'expired';
  time_remaining_ms: number | null;
  elapsed_ms?: number;
  timer_started_at?: string;
  state_version: number;
}

export async function getAuctionTimerState(
  matchId: string
): Promise<CommandResult<AuctionTimerState>> {
  return executeCommand<AuctionTimerState>('rpc_get_auction_state', {
    p_match_id: matchId,
  });
}

/**
 * Helper: Poll auction timer state
 * Returns a cleanup function
 */
export function pollAuctionTimer(
  matchId: string,
  onUpdate: (state: AuctionTimerState) => void,
  intervalMs: number = 250
): () => void {
  let active = true;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const poll = async () => {
    if (!active) return;

    // Use getSupabaseClient (sync, no auth overhead) for hot polling path
    const supabase = getSupabaseClient();
    if (!supabase) {
      if (active) timeoutId = setTimeout(poll, intervalMs);
      return;
    }

    const { data, error } = await supabase.rpc('rpc_get_auction_state', { p_match_id: matchId });

    if (active && !error && data && !data.error) {
      onUpdate(data as AuctionTimerState);
    }

    if (active) {
      timeoutId = setTimeout(poll, intervalMs);
    }
  };

  poll();

  return () => {
    active = false;
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
}

/**
 * Battle API
 */

export interface CharacterSelectionResult {
  character_id: string;
  waiting_for_opponent: boolean;
}

export async function submitCharacterSelectionV2(
  matchId: string,
  characterId: string
): Promise<CommandResult<CharacterSelectionResult>> {
  return executeCommand<CharacterSelectionResult>(
    'rpc_submit_character_selection_v2',
    {
      p_match_id: matchId,
      p_character_id: characterId,
    },
    { idempotent: true, matchId }
  );
}

export interface BattleRoundResult {
  round_winner: 'host' | 'guest' | 'tie';
  host_wins: number;
  guest_wins: number;
  match_winner: 'host' | 'guest' | null;
  match_over: boolean;
  next_stat?: string | null;
  new_state_version: number;
}

export async function resolveBattleRoundV2(
  matchId: string,
  hostStatValue: number,
  guestStatValue: number,
  expectedStateVersion?: number,
  nextStat?: string | null
): Promise<CommandResult<BattleRoundResult>> {
  return executeCommand<BattleRoundResult>(
    'rpc_resolve_battle_round_v2',
    {
      p_match_id: matchId,
      p_host_stat_value: hostStatValue,
      p_guest_stat_value: guestStatValue,
      p_expected_state_version: expectedStateVersion,
      p_next_stat: nextStat ?? null,
    },
    { idempotent: true, matchId }
  );
}

/**
 * Fast character lock - optimized for speed with minimal database overhead
 */
export async function quickLockSelectionV2(
  matchId: string,
  characterId: string
): Promise<CommandResult<{ character_id: string }>> {
  return executeCommand<{ character_id: string }>(
    'rpc_quick_lock_selection',
    {
      p_match_id: matchId,
      p_character_id: characterId,
    },
    { idempotent: false, matchId }
  );
}

/**
 * Fast bid lock - optimized for speed
 */
export async function quickLockBidV2(
  matchId: string
): Promise<CommandResult<{ locked_bid: number }>> {
  return executeCommand<{ locked_bid: number }>(
    'rpc_quick_lock_bid',
    {
      p_match_id: matchId,
    },
    { idempotent: false, matchId }
  );
}
